import JSZip from "jszip";
import type { DailyRedesignRow } from "../store/superAdmin/dataExtractSlice";

const COLUMNS: { key: keyof DailyRedesignRow; label: string; width: number }[] = [
  { key: "date", label: "Date", width: 14 },
  { key: "lab_name", label: "Lab Name", width: 12 },
  { key: "case_name", label: "Case Name", width: 36 },
  { key: "allocated_to", label: "Allocated to", width: 22 },
  { key: "qc_capt", label: "QC CAPT", width: 18 },
  { key: "qc_on_qc", label: "QC ON QC", width: 18 },
  { key: "unit", label: "Unit", width: 10 },
  { key: "grade", label: "Grade", width: 10 },
  { key: "category", label: "CATEGORY", width: 32 },
  { key: "original_designer", label: "Original Designer", width: 22 },
  { key: "original_qc", label: "Original QC", width: 18 },
  { key: "original_qc_on_qc", label: "Original QC on QC", width: 20 },
  { key: "internal_external", label: "Internal / External", width: 18 },
  { key: "rd_no", label: "RD, No", width: 12 },
  { key: "discussion", label: "Discussion", width: 16 },
  { key: "year_month", label: "Year-Month", width: 14 },
  { key: "category_designer", label: "Category Designer", width: 18 },
  { key: "notes", label: "Notes", width: 42 },
];

type CategoryPaint = { fill: string; font: string };

/** Cell fill and text colors matching the category dropdowns. */
const CATEGORY_COLORS: Record<string, CategoryPaint> = {
  Query: { fill: "D4EDBC", font: "11734B" },
  "Design Preference": { fill: "BFE1F6", font: "0A53A8" },
  "Note Missed": { fill: "FFCFC9", font: "C84848" },
  "Design Mistake": { fill: "FFCFC9", font: "B10202" },
  "Files Mistake": { fill: "FFCFC9", font: "BC1E1E" },
  "File Mistake": { fill: "FFCFC9", font: "BC1E1E" },
  "Client Preference Missed": { fill: "FFE5A0", font: "706040" },
  "Minor Design Mistake": { fill: "D4EDBC", font: "298259" },
  "Need to Improve Design Quality": { fill: "FFE5A0", font: "604830" },
  Other: { fill: "E5E7EB", font: "4B5563" },
  Others: { fill: "E5E7EB", font: "4B5563" },
  "Others (note required)": { fill: "E5E7EB", font: "4B5563" },
};

const CATEGORY_OPTIONS = [
  "Query",
  "Design Preference",
  "Note Missed",
  "Design Mistake",
  "Files Mistake",
  "Client Preference Missed",
  "Minor Design Mistake",
  "Need to Improve Design Quality",
  "Other",
];

const HEADER_FILL = "1F4E79";
const DEFAULT_CATEGORY: CategoryPaint = { fill: "F3F4F6", font: "1F2937" };

const paintForCategory = (name: string): CategoryPaint | null => {
  if (!name) return null;
  if (CATEGORY_COLORS[name]) return CATEGORY_COLORS[name];
  const key = Object.keys(CATEGORY_COLORS).find(
    (label) => label.toLowerCase() === name.toLowerCase(),
  );
  return key ? CATEGORY_COLORS[key] : DEFAULT_CATEGORY;
};

const xmlEscape = (value: string) =>
  String(value ?? "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const colLetter = (index: number) => {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

const primaryCategory = (value: string) =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .find(Boolean) || "";

const cellText = (row: DailyRedesignRow, key: keyof DailyRedesignRow) => {
  const value = row[key];
  if (value == null) return "";
  return String(value);
};

const isNumberColumn = (key: keyof DailyRedesignRow) => key === "unit" || key === "grade";

const buildStyles = (categoryPaints: CategoryPaint[]) => {
  const fills = [
    `<fill><patternFill patternType="none"/></fill>`,
    `<fill><patternFill patternType="gray125"/></fill>`,
    `<fill><patternFill patternType="solid"><fgColor rgb="FF${HEADER_FILL}"/></patternFill></fill>`,
    ...categoryPaints.map(
      (paint) =>
        `<fill><patternFill patternType="solid"><fgColor rgb="FF${paint.fill}"/></patternFill></fill>`,
    ),
  ];
  const fonts = [
    `<font><sz val="11"/><color rgb="FF1F2937"/><name val="Calibri"/><family val="2"/></font>`,
    `<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>`,
    ...categoryPaints.map(
      (paint) =>
        `<font><sz val="11"/><color rgb="FF${paint.font}"/><name val="Calibri"/><family val="2"/></font>`,
    ),
  ];
  const dxfs = CATEGORY_OPTIONS.map((name) => {
    const paint = CATEGORY_COLORS[name];
    return `<dxf><font><color rgb="FF${paint.font}"/></font><fill><patternFill patternType="solid"><fgColor rgb="FF${paint.fill}"/><bgColor rgb="FF${paint.fill}"/></patternFill></fill></dxf>`;
  });
  const headerXf = `<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center" horizontal="center"/></xf>`;
  const textXf = `<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf>`;
  const categoryXfs = categoryPaints.map(
    (_, index) =>
      `<xf numFmtId="0" fontId="${index + 2}" fillId="${index + 3}" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf>`,
  );
  const xfs = [
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>`,
    headerXf,
    textXf,
    ...categoryXfs,
  ];

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="${fonts.length}">
    ${fonts.join("")}
  </fonts>
  <fills count="${fills.length}">${fills.join("")}</fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFE5E7EB"/></left>
      <right style="thin"><color rgb="FFE5E7EB"/></right>
      <top style="thin"><color rgb="FFE5E7EB"/></top>
      <bottom style="thin"><color rgb="FFE5E7EB"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="${xfs.length}">${xfs.join("")}</cellXfs>
  <dxfs count="${dxfs.length}">${dxfs.join("")}</dxfs>
</styleSheet>`;
};

const strCell = (ref: string, text: string, style: number) => {
  const space = /^\s|\s$|\n/.test(text) ? ` xml:space="preserve"` : "";
  return `<c r="${ref}" t="inlineStr" s="${style}"><is><t${space}>${xmlEscape(text)}</t></is></c>`;
};

const numCell = (ref: string, value: number, style: number) =>
  `<c r="${ref}" s="${style}"><v>${value}</v></c>`;

const buildDailyRedesignZip = (rows: DailyRedesignRow[]) => {
  const usedPaints: CategoryPaint[] = [];
  const paintIndex = new Map<string, number>();
  const rememberPaint = (paint: CategoryPaint) => {
    const key = `${paint.fill}|${paint.font}`;
    const existing = paintIndex.get(key);
    if (existing != null) return existing;
    const index = usedPaints.length;
    usedPaints.push(paint);
    paintIndex.set(key, index);
    return index;
  };

  const categoryStyle = (category: string) => {
    const paint = paintForCategory(primaryCategory(category));
    if (!paint) return 2;
    return rememberPaint(paint) + 3;
  };

  const header = COLUMNS.map((col, index) =>
    strCell(`${colLetter(index)}1`, col.label, 1),
  ).join("");

  const body = rows.map((row, rowIndex) => {
    const excelRow = rowIndex + 2;
    const categoryCol = COLUMNS.findIndex((col) => col.key === "category");
    const cells = COLUMNS.map((col, index) => {
      const ref = `${colLetter(index)}${excelRow}`;
      const style = index === categoryCol ? categoryStyle(row.category) : 2;
      if (isNumberColumn(col.key)) {
        const value = row[col.key];
        if (typeof value === "number" && Number.isFinite(value)) {
          return numCell(ref, value, style);
        }
        return strCell(ref, "", style);
      }
      return strCell(ref, cellText(row, col.key), style);
    });
    return `<row r="${excelRow}">${cells.join("")}</row>`;
  });

  const cols = COLUMNS.map(
    (col, index) =>
      `<col min="${index + 1}" max="${index + 1}" width="${col.width}" customWidth="1"/>`,
  ).join("");
  const lastCol = colLetter(COLUMNS.length - 1);
  const lastRow = Math.max(rows.length + 1, 1);
  const categoryCol = colLetter(COLUMNS.findIndex((col) => col.key === "category"));
  const categorySqref = `${categoryCol}2:${categoryCol}${Math.max(lastRow, 2)}`;
  const categoryRules = CATEGORY_OPTIONS.map(
    (name, index) =>
      `<cfRule type="cellIs" dxfId="${index}" priority="${index + 1}" operator="equal"><formula>"${xmlEscape(name)}"</formula></cfRule>`,
  ).join("");
  const categoryList = xmlEscape(CATEGORY_OPTIONS.join(","));

  const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:${lastCol}${lastRow}"/>
  <sheetViews><sheetView tabSelected="1" workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <cols>${cols}</cols>
  <sheetData>
    <row r="1" ht="22" customHeight="1">${header}</row>
    ${body.join("")}
  </sheetData>
  <autoFilter ref="A1:${lastCol}${lastRow}"/>
  <conditionalFormatting sqref="${categorySqref}">${categoryRules}</conditionalFormatting>
  <dataValidations count="1">
    <dataValidation type="list" allowBlank="1" showDropDown="0" showErrorMessage="1" sqref="${categorySqref}">
      <formula1>"${categoryList}"</formula1>
    </dataValidation>
  </dataValidations>
</worksheet>`;

  const zip = new JSZip();
  zip.file(
    "[Content_Types].xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`,
  );
  zip.file(
    "_rels/.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
  );
  zip.file(
    "xl/workbook.xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <workbookViews><workbookView activeTab="0"/></workbookViews>
  <sheets><sheet name="Daily Redesign" sheetId="1" r:id="rId1"/></sheets>
</workbook>`,
  );
  zip.file(
    "xl/_rels/workbook.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
  );
  zip.file("xl/styles.xml", buildStyles(usedPaints));
  zip.file("xl/worksheets/sheet1.xml", sheetXml);
  return zip;
};

export const downloadDailyRedesignWorkbook = async (
  rows: DailyRedesignRow[],
  filename: string,
) => {
  const zip = buildDailyRedesignZip(rows);
  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
